import React from 'react';
import { Icon, Member } from 'components/common/Elements/Elements';
import { access, resources, hasAccess } from 'utils/permissions';

import {
  GridColDef,
  useRightSideBar,
  Button,
  Typography,
  Tooltip,
  ResponsiveChipsGroup,
  Chips
} from '@p81-common/ui';
import {
  changeIconDivice,
  enableDBAccess,
  getInitialsFull,
  handleAgent,
  handlePlatform
} from 'utils/common';
import { links, platformRoles } from 'data/constants';
import SideBarBody from '../parts/SideBarBody/SideBarBody';
import type { HandleChangeRoleMenu, User } from './types';

export const createTableConfig: (tableConfigOptions: {
  handleChangeRoleMenu: HandleChangeRoleMenu;
}) => GridColDef[] = ({ handleChangeRoleMenu }) => [
  {
    show: true,
    isSortable: true,
    field: 'firstName',
    title: 'Member',
    width: 267,
    type: 'userName',
    fieldsToExport: ['firstName', 'lastName'],
    renderCell(params) {
      const { row } = params;
      const name = `${row.firstName} ${row.lastName}`;
      const member = {
        fullName: name,
        description: row.email,
        initials: getInitialsFull(name),
        initialsColor: row.initialsColor
      };
      return <Member member={member} />;
    }
  },
  {
    show: true,
    isSortable: false,
    field: 'userid',
    title: 'User ID',
    width: 155,
    type: 'textList',
    fieldsToExport: ['userid']
  },
  {
    show: true,
    isSortable: true,
    field: 'roles',
    title: 'Role',
    width: 180,
    type: 'textList',
    fieldsToExport: ['roles', 'role'],
    renderCell(params) {
      const { row } = params;
      return row?.roles?.length > 0 ? (
        <ResponsiveChipsGroup items={row.roles} />
      ) : (
        <Chips label={row.role} sx={{ minWidth: 'unset' }} />
      );
    }
  },
  {
    show: true,
    isSortable: true,
    field: 'groups',
    title: 'Groups',
    width: 155,
    type: 'textList',
    fieldsToExport: ['groups'],
    renderCell(params) {
      const { row } = params;
      const { setOpenSecondary, setComponentSecondaryDrawer } =
        useRightSideBar();
      if (row?.groups?.length === 1) {
        return (
          <Tooltip title={row.groups[0].id} placement="top">
            <Typography>{row.groups[0].name}</Typography>
          </Tooltip>
        );
      } else {
        return (
          <Button
            variant="text"
            onClick={() => {
              setComponentSecondaryDrawer(<SideBarBody groups={row.groups} />);
              setOpenSecondary(true);
            }}
          >
            more...
          </Button>
        );
      }
    }
  },
  {
    show: true,
    isSortable: true,
    field: 'devices',
    title: 'Devices',
    width: 105,
    type: 'textList',
    fieldsToExport: ['devices'],
    renderCell(params) {
      const { row } = params;
      return <Typography>{row.devices}</Typography>;
    }
  },
  {
    show: true,
    isSortable: true,
    field: 'idp',
    title: 'IDP',
    width: 155,
    type: 'textList',
    fieldsToExport: ['idp'],
    renderCell(params) {
      const { row } = params;
      const idp = row?.idp?.join(', ');
      return <Typography>{idp || 'NA'}</Typography>;
    }
  },
  {
    show: true,
    isSortable: true,
    field: 'lastVpnLogin',
    title: 'Last Agent Login',
    width: 205,
    type: 'dateCreated',
    fieldsToExport: ['lastVpnLogin'],
    renderCell(params) {
      const { row } = params;
      if (!row.lastVpnLogin) return <Typography>NA</Typography>;
      return <Typography fontSize={14}>{row.lastVpnLogin}</Typography>;
    }
  },
  {
    show: true,
    isSortable: true,
    field: 'lastAppLogin',
    title: 'Last Platform Login',
    width: 205,
    type: 'dateCreated',
    fieldsToExport: ['lastAppLogin'],
    renderCell(params) {
      const { row } = params;
      if (!row.lastAppLogin) return <Typography>NA</Typography>;
      return <Typography fontSize={14}>{row.lastAppLogin}</Typography>;
    }
  },
  {
    field: 'actions',
    cellClassName: 'actions',
    title: '',
    show: true,
    width: 165,
    fieldsToExport: [],
    align: 'left',
    actions: [
      {
        disableHover: true,
        iconSrc: `${links.imagesBaseUrl}/icons/icon_product.svg`,
        tooltipText: 'Platform Support Access',
        value: 'platformSupportAccess',
        onClick: (value, row) => () => {
          handlePlatform(row.email, row.customerId);
        },
        hide: row =>
          !row.hasSupportAccess ||
          !hasAccess([resources.CUSTOMER_TEAM_SUPPORT, access.READ])
      },
      {
        disableHover: true,
        iconSrc: `${links.imagesBaseUrl}/icons3/icon_agent.svg`,
        tooltipText: 'Agent Support Access',
        value: 'agentSupportAccess',
        onClick: (value, row) => () => {
          handleAgent(row.email, row.customerId);
        },
        hide: row =>
          !row.hasSupportAccess ||
          !hasAccess([resources.CUSTOMER_TEAM_SUPPORT, access.READ])
      },
      {
        disableHover: true,
        iconSrc: `${links.imagesBaseUrl}/icons2/icon_userSettings.svg`,
        tooltipText: 'Change Role',
        value: 'changeUserRole',
        onClick: (value, row) => () => {
          handleChangeRoleMenu(row as unknown as User);
        },
        hide: row =>
          row.role === platformRoles.ADMIN_DEMO ||
          !hasAccess([resources.CUSTOMER_TEAM_ROLE_UPDATE, access.WRITE])
      },
      {
        disableHover: true,
        iconSrc: `${links.imagesBaseUrl}/icons3/icon_database.svg`,
        tooltipText: 'Enable DB Access',
        value: 'enableDbAccess',
        onClick: (value, row) => async () => {
          await enableDBAccess(row);
        },
        hide: row =>
          !!row.hasDbAccessEnabled ||
          !hasAccess([resources.CUSTOMER_TEAM_ENABLE_DB_ACCESS, access.WRITE])
      }
    ]
  }
];

export const masterDetailConfig: GridColDef[] = [
  {
    show: true,
    isSortable: false,
    field: 'os',
    title: 'OS',
    width: 155,
    type: 'textList',
    fieldsToExport: ['os'],
    renderCell(params) {
      const { row } = params;
      return (
        <>
          <Icon
            size="16px"
            icon={changeIconDivice(row.os, row.osVersion)}
            color="#3a5ba2"
          />
          <Typography marginLeft={1}>{row.os}</Typography>
        </>
      );
    }
  },
  {
    show: true,
    isSortable: false,
    field: 'deviceType',
    title: 'Device Type',
    width: 155,
    type: 'textList',
    fieldsToExport: ['deviceType']
  },
  {
    show: true,
    isSortable: false,
    field: 'appVersion',
    title: 'App Version',
    width: 155,
    type: 'textList',
    fieldsToExport: ['appVersion']
  },
  {
    show: true,
    isSortable: false,
    field: 'hostname',
    title: 'Host Name',
    width: 155,
    type: 'textList',
    fieldsToExport: ['hostname']
  },
  {
    show: true,
    isSortable: false,
    field: 'localeIdentifier',
    title: 'Local Identifier',
    width: 155,
    type: 'textList',
    fieldsToExport: ['localeIdentifier']
  },
  {
    show: true,
    isSortable: false,
    field: 'timezone',
    title: 'Timezone',
    width: 155,
    type: 'textList',
    fieldsToExport: ['timezone']
  },
  {
    show: true,
    isSortable: false,
    field: 'lastVpnLogin',
    title: 'Last Agent Login',
    width: 155,
    type: 'dateCreated',
    fieldsToExport: ['lastVpnLogin'],
    renderCell(params) {
      const { row } = params;
      if (!row.lastVpnLogin) return <Typography>NA</Typography>;
      return <Typography fontSize={14}>{row.lastVpnLogin}</Typography>;
    }
  }
];

export const defaultFilters = {
  customerId: '*',
  search: '*',
  role: '*',
  idp: '*',
  'groups.name': '*',
  lastVpnLogin: '*',
  lastAppLogin: '*'
};
